import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { notifyApiError } from "@/lib/api-error";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { CalendarIntegrationSettings } from "@/components/interviews/CalendarIntegrationSettings";
import {
  type BookingResult,
  type BookingSettings,
  type Connection,
  type Profile,
  type EventType,
  type Provider,
  type TimeWindow,
  providerName,
  fieldClass,
  panelClass,
} from "@/components/bookings/types";
const allProviders: Provider[] = ["titan", "google", "microsoft"];
const newType = (ready: Provider[]): EventType => ({
  id: "new",
  title: "",
  duration: 30,
  buffer_before: 0,
  buffer_after: 10,
  notice_hours: 24,
  horizon_days: 30,
  daily_limit: 8,
  providers: ready,
  enabled: true,
});
const toMinutes = (t: string) => {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
};
const toTime = (minutes: number) =>
  `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
// Mirrors the backend save rule: windows on one day must not overlap.
const overlapping = (windows: TimeWindow[]) => {
  const sorted = [...windows].sort((a, b) => a.start.localeCompare(b.start));
  return sorted.some((w, i) => i > 0 && w.start < sorted[i - 1].end);
};
// Suggest the next free window after the day's latest one, or null when the day is full.
const nextWindow = (windows: TimeWindow[]): TimeWindow | null => {
  const latest = Math.max(...windows.map((w) => toMinutes(w.end)));
  const start = latest + 60;
  if (start + 30 > 23 * 60 + 59) return null;
  return { start: toTime(start), end: toTime(Math.min(start + 120, 23 * 60 + 59)) };
};
interface Appointment {
  id: string;
  guest_name: string;
  guest_email: string;
  starts_at: string;
  status: string;
  provider: Provider;
  meeting_url: string | null;
  last_error: string | null;
  event_type: { title: string };
}
interface DirectoryMember {
  slug: string;
  display_name: string;
  timezone: string;
}
interface Smtp {
  host: string;
  port: number;
  secure: boolean;
  username: string;
  from_email: string;
  passwordConfigured?: boolean;
}
const days = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];
function SmtpSettings() {
  const [value, setValue] = useState<Smtp>({
    host: "",
    port: 587,
    secure: false,
    username: "",
    from_email: "",
  });
  const [password, setPassword] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [working, setWorking] = useState(false);
  const [message, setMessage] = useState("");
  useEffect(() => {
    void api
      .get<Smtp | null>("/bookings/smtp")
      .then((s) => {
        if (s) setValue(s);
        setLoaded(true);
      })
      .catch(notifyApiError);
  }, []);
  return (
    <form
      className={panelClass}
      onSubmit={(e) => {
        e.preventDefault();
        setWorking(true);
        void api
          .put("/bookings/smtp", {
            ...value,
            passwordConfigured: undefined,
            ...(password ? { password } : {}),
          })
          .then(() => {
            setPassword("");
            setValue((v) => ({ ...v, passwordConfigured: true }));
            setMessage("Email settings saved.");
          })
          .catch(notifyApiError)
          .finally(() => setWorking(false));
      }}
    >
      <h2 className="font-semibold text-xl">Booking email delivery</h2>
      <p className="text-text-secondary text-sm">
        SMTP sends booking notices and reminders, plus calendar invitations for
        Titan. Google and Microsoft send their own invitations.
      </p>
      <fieldset
        disabled={!loaded || working}
        className="grid sm:grid-cols-2 gap-4"
      >
        {(["host", "username", "from_email"] as const).map((key) => (
          <label key={key}>
            {
              {
                host: "SMTP hostname",
                username: "Username",
                from_email: "Sender email",
              }[key]
            }
            <input
              required
              type={key === "from_email" ? "email" : "text"}
              className={fieldClass}
              value={value[key]}
              onChange={(e) => setValue({ ...value, [key]: e.target.value })}
            />
          </label>
        ))}
        <label>
          Encryption
          <select
            className={fieldClass}
            value={value.port}
            onChange={(e) =>
              setValue({
                ...value,
                port: Number(e.target.value),
                secure: e.target.value === "465",
              })
            }
          >
            <option value={587}>STARTTLS · port 587</option>
            <option value={465}>TLS · port 465</option>
          </select>
        </label>
        <label>
          Password
          <input
            autoComplete="new-password"
            required={!value.passwordConfigured}
            type="password"
            className={fieldClass}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={
              value.passwordConfigured ? "Saved — leave blank to keep" : ""
            }
          />
        </label>
      </fieldset>
      <Button disabled={!loaded || working}>Save email settings</Button>
      <Button
        type="button"
        variant="outline"
        disabled={!loaded || working || !value.passwordConfigured}
        onClick={() => {
          setWorking(true);
          void api
            .post("/bookings/smtp/test")
            .then(() =>
              setMessage("Test email sent to your CRM email address."),
            )
            .catch(notifyApiError)
            .finally(() => setWorking(false));
        }}
      >
        Send test email to me
      </Button>
      {message && <p role="status">{message}</p>}
    </form>
  );
}
export function BookingsPage() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [tab, setTab] = useState("appointments");
  const [loaded, setLoaded] = useState(false);
  const [working, setWorking] = useState(false);
  const [notice, setNotice] = useState("");
  const [profile, setProfile] = useState<Profile>({
    slug: "",
    display_name: "",
    timezone: "Africa/Johannesburg",
    enabled: false,
    hours: [1, 2, 3, 4, 5].map((day) => ({
      day,
      windows: [{ start: "09:00", end: "17:00" }],
    })),
    overrides: [],
    event_types: [],
  });
  const [saved, setSaved] = useState(false);
  const [webOrigin, setWebOrigin] = useState("");
  // Publish state as last saved, so the status never reflects an unsaved tick.
  const [published, setPublished] = useState(false);
  const [copied, setCopied] = useState(false);
  const [connections, setConnections] = useState<Connection[]>([]);
  const ready = connections
    .filter((c) => c.guestReady)
    .map((c) => c.provider);
  const [editing, setEditing] = useState<EventType>(newType([]));
  const [typeSaved, setTypeSaved] = useState("");
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [directory, setDirectory] = useState<DirectoryMember[]>([]);
  const applyConnections = (list: Connection[]) => {
    setConnections(list);
    // An untouched new type defaults to every platform guests can book now.
    setEditing((e) =>
      e.id === "new" && !e.providers.length
        ? {
            ...e,
            providers: list.filter((c) => c.guestReady).map((c) => c.provider),
          }
        : e,
    );
  };
  const reload = async () => {
    const [s, a, d] = await Promise.all([
      api.get<BookingSettings>("/bookings/me"),
      api.get<Appointment[]>("/bookings/appointments"),
      api.get<DirectoryMember[]>("/bookings/directory"),
    ]);
    applyConnections(s.providers);
    if (s.profile) {
      setProfile(s.profile);
      setPublished(s.profile.enabled);
      setSaved(true);
    } else {
      setProfile((p) => ({
        ...p,
        display_name: `${user?.firstName || ""} ${user?.lastName || ""}`.trim(),
      }));
    }
    setWebOrigin(s.webOrigin);
    setAppointments(a);
    setDirectory(d);
    setLoaded(true);
    return s;
  };
  // Calendars are connected in a separate component, so refresh platform readiness on demand.
  const refreshConnections = () =>
    api
      .get<BookingSettings>("/bookings/me")
      .then((s) => applyConnections(s.providers))
      .catch(notifyApiError);
  const bookingLink = `${webOrigin}/book/${profile.slug}`;
  const hiddenReason = (t: EventType) =>
    !t.enabled
      ? "Paused — hidden from guests"
      : !t.providers.some((p) => ready.includes(p))
        ? "Hidden from guests — no ticked platform is connected"
        : "";
  const storedType = profile.event_types.find((t) => t.id === editing.id);
  // Platform order is not meaningful, so compare it sorted.
  const typeKey = (t: EventType) =>
    JSON.stringify({ ...t, providers: [...t.providers].sort() });
  const typeDirty = !storedType || typeKey(storedType) !== typeKey(editing);
  const overlapDays = profile.hours
    .filter((d) => overlapping(d.windows))
    .map((d) => d.day);
  useEffect(() => {
    void reload().catch(notifyApiError);
  }, []);
  const run = async (action: () => Promise<void>) => {
    setWorking(true);
    setNotice("");
    try {
      await action();
    } catch (e) {
      notifyApiError(e);
    } finally {
      setWorking(false);
    }
  };
  const setDay = (
    day: number,
    windows: Array<{ start: string; end: string }>,
  ) =>
    setProfile((p) => ({
      ...p,
      hours: [...p.hours.filter((d) => d.day !== day), { day, windows }],
    }));
  return (
    <div className="space-y-6">
      <header>
        <p className="text-emerald-400 text-sm">YOUR TIME, CONNECTED</p>
        <h1 className="text-3xl font-semibold text-text-primary">Bookings</h1>
        <p className="text-text-secondary mt-2">
          Share your booking link, meet with a colleague, and manage your
          availability.
        </p>
      </header>
      {loaded && saved && (
        <section
          aria-label="Your booking link"
          className="rounded-xl border border-white/10 bg-bg-secondary p-5 flex flex-wrap items-center gap-3"
        >
          <span
            className={
              "rounded-full px-3 py-1 text-sm font-semibold " +
              (published
                ? "bg-emerald-500/20 text-emerald-300"
                : "bg-amber-500/20 text-amber-300")
            }
          >
            {published ? "Published" : "Not published"}
          </span>
          <a
            className="text-emerald-400 underline break-all"
            href={bookingLink}
            target="_blank"
            rel="noreferrer"
          >
            {bookingLink}
          </a>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() =>
              void navigator.clipboard.writeText(bookingLink).then(
                () => {
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                },
                notifyApiError,
              )
            }
          >
            {copied ? "Copied" : "Copy link"}
          </Button>
          <p className="w-full text-sm text-text-secondary">
            {published
              ? "Share this link with guests. They do not need a CRM login."
              : "Guests who open this link see “Booking page not found”. Tick Publish booking page under Availability and save."}
          </p>
        </section>
      )}
      {params.get("calendar") && (
        <p role="status" className={panelClass}>
          {params.get("calendar") === "connected"
            ? "Calendar connected. Open Calendars and choose your destination and conflict calendars."
            : "Calendar connection was not completed. Try again from Calendars."}
        </p>
      )}
      <nav className="flex flex-wrap gap-2" aria-label="Booking sections">
        {[
          "appointments",
          "team",
          "availability",
          "meeting types",
          "calendars",
          ...(user?.role === "admin" ? ["email"] : []),
        ].map((t) => (
          <Button
            key={t}
            variant={t === tab ? "default" : "outline"}
            onClick={() => {
              setTab(t);
              setNotice("");
              if (t === "meeting types") void refreshConnections();
            }}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </Button>
        ))}
      </nav>
      {notice && (
        <p role="status" className="text-emerald-400">
          {notice}
        </p>
      )}
      {!loaded && <p>Loading booking settings…</p>}
      {tab === "calendars" && <CalendarIntegrationSettings />}
      {tab === "email" && user?.role === "admin" && <SmtpSettings />}
      {loaded && tab === "appointments" && (
        <section className={panelClass}>
          <div className="flex justify-between gap-3">
            <h2 className="text-xl font-semibold">Your appointments</h2>
            <Button
              variant="outline"
              onClick={() => void run(async () => {
                  await reload();
                })}
              disabled={working}
            >
              Refresh
            </Button>
          </div>
          {!appointments.length && (
            <p className="text-text-secondary">
              No bookings yet. Set your availability, connect a calendar and add
              a meeting type to get started.
            </p>
          )}
          {appointments.map((a) => (
            <article
              key={a.id}
              className="border-t border-white/10 pt-4 space-y-2"
            >
              <div className="flex flex-wrap justify-between gap-2">
                <h3 className="font-semibold">
                  {a.event_type.title} · {a.guest_name}
                </h3>
                <span className="text-emerald-400">{a.status}</span>
              </div>
              <p className="text-sm text-text-secondary">
                {new Date(a.starts_at).toLocaleString()} ·{" "}
                {providerName(a.provider)}
              </p>
              <p className="text-sm break-all">{a.guest_email}</p>
              {a.last_error && (
                <p className="text-amber-300 text-sm">{a.last_error}</p>
              )}
              <div className="flex flex-wrap gap-3">
                {a.meeting_url && a.status === "confirmed" && (
                  <a
                    href={a.meeting_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-emerald-400 underline"
                  >
                    Join meeting
                  </a>
                )}
                {a.status === "confirmed" && (
                  <Button
                    variant="outline"
                    disabled={working}
                    onClick={() =>
                      void run(async () => {
                        const b = await api.post<BookingResult>(
                          `/bookings/appointments/${a.id}/manage`,
                        );
                        window.location.assign(`/book/manage#${b.manageToken}`);
                      })
                    }
                  >
                    Manage / reschedule
                  </Button>
                )}
                {a.status === "confirmed" && (
                  <Button
                    variant="ghost"
                    disabled={working}
                    onClick={() =>
                      void run(async () => {
                        await api.post(`/bookings/appointments/${a.id}/cancel`);
                        await reload();
                      })
                    }
                  >
                    Cancel
                  </Button>
                )}
                {["pending", "rescheduling", "cancelling"].includes(
                  a.status,
                ) && (
                  <Button
                    variant="outline"
                    disabled={working}
                    onClick={() =>
                      void run(async () => {
                        await api.post(`/bookings/appointments/${a.id}/retry`);
                        await reload();
                      })
                    }
                  >
                    Retry calendar update
                  </Button>
                )}
              </div>
            </article>
          ))}
        </section>
      )}
      {loaded && tab === "team" && (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {directory.map((d) => (
            <article className={panelClass} key={d.slug}>
              <h2 className="font-semibold">{d.display_name}</h2>
              <p className="text-text-secondary text-sm">{d.timezone}</p>
              <Link
                className="text-emerald-400 underline"
                to={`/book/${d.slug}`}
              >
                Book time
              </Link>
            </article>
          ))}
          {!directory.length && <p>No team booking pages are published yet.</p>}
        </div>
      )}
      {loaded && tab === "availability" && (
        <form
          className={panelClass}
          onSubmit={(e) => {
            e.preventDefault();
            void run(async () => {
              const { event_types: _types, ...input } = profile;
              await api.put("/bookings/profile", input);
              await reload();
              setNotice("Booking profile saved.");
            });
          }}
        >
          <h2 className="text-xl font-semibold">Your booking page</h2>
          <fieldset disabled={working} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <label>
                Display name
                <input
                  required
                  maxLength={150}
                  className={fieldClass}
                  value={profile.display_name}
                  onChange={(e) =>
                    setProfile({ ...profile, display_name: e.target.value })
                  }
                />
              </label>
              <label>
                Booking address
                <input
                  required
                  pattern="[a-z0-9]+(-[a-z0-9]+)*"
                  minLength={3}
                  maxLength={100}
                  placeholder="bret-wurdeman"
                  className={fieldClass}
                  value={profile.slug}
                  onChange={(e) =>
                    setProfile({ ...profile, slug: e.target.value })
                  }
                />
              </label>
              <label>
                Host timezone
                <input
                  required
                  className={fieldClass}
                  value={profile.timezone}
                  onChange={(e) =>
                    setProfile({ ...profile, timezone: e.target.value })
                  }
                />
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={profile.enabled}
                  onChange={(e) =>
                    setProfile({ ...profile, enabled: e.target.checked })
                  }
                />
                Publish booking page
              </label>
            </div>
            <p className="text-sm text-text-secondary">
              {profile.enabled
                ? "Ticked: guests can open your link and book you."
                : "Unticked: guests who open your link see “Booking page not found”."}
              {profile.enabled !== published &&
                saved &&
                " This change is not saved yet."}
            </p>
            <h3 className="font-semibold">Weekly hours</h3>
            <p className="text-sm text-text-secondary">
              Times use your host timezone. Split a day into multiple windows
              for a lunch break.
            </p>
            {days.map((day, index) => {
              const windows =
                profile.hours.find((d) => d.day === index)?.windows || [];
              return (
                <div
                  key={day}
                  className="flex flex-wrap items-center gap-3 border-t border-white/10 py-2"
                >
                  <label className="w-32 flex gap-2 items-center">
                    <input
                      type="checkbox"
                      checked={!!windows.length}
                      onChange={(e) =>
                        setDay(
                          index,
                          e.target.checked
                            ? [{ start: "09:00", end: "17:00" }]
                            : [],
                        )
                      }
                    />
                    {day}
                  </label>
                  {windows.map((w, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        aria-label={`${day} start ${i + 1}`}
                        type="time"
                        required
                        className={fieldClass}
                        value={w.start}
                        onChange={(e) =>
                          setDay(
                            index,
                            windows.map((v, j) =>
                              j === i ? { ...v, start: e.target.value } : v,
                            ),
                          )
                        }
                      />
                      <span>to</span>
                      <input
                        aria-label={`${day} end ${i + 1}`}
                        type="time"
                        required
                        className={fieldClass}
                        value={w.end}
                        onChange={(e) =>
                          setDay(
                            index,
                            windows.map((v, j) =>
                              j === i ? { ...v, end: e.target.value } : v,
                            ),
                          )
                        }
                      />
                      <button
                        type="button"
                        aria-label={`Remove ${day} window ${i + 1}`}
                        onClick={() =>
                          setDay(
                            index,
                            windows.filter((_, j) => j !== i),
                          )
                        }
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  {!!windows.length &&
                    windows.length < 4 &&
                    nextWindow(windows) && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          const next = nextWindow(windows);
                          if (next) setDay(index, [...windows, next]);
                        }}
                      >
                        Add window
                      </Button>
                    )}
                  {overlapDays.includes(index) && (
                    <p role="alert" className="w-full text-sm text-red-400">
                      These {day} times overlap. Change them so each window
                      ends before the next one starts.
                    </p>
                  )}
                </div>
              );
            })}
            <h3 className="font-semibold">Date exceptions</h3>
            <p className="text-sm text-text-secondary">
              Block holidays or days off. An exception replaces the weekly hours
              for that date.
            </p>
            {profile.overrides.map((o, i) => (
              <div className="flex flex-wrap gap-2" key={i}>
                <input
                  aria-label={`Exception date ${i + 1}`}
                  type="date"
                  required
                  className={fieldClass + " max-w-48"}
                  value={o.date}
                  onChange={(e) =>
                    setProfile({
                      ...profile,
                      overrides: profile.overrides.map((v, j) =>
                        j === i ? { ...v, date: e.target.value } : v,
                      ),
                    })
                  }
                />
                <span className="py-2">Unavailable</span>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() =>
                    setProfile({
                      ...profile,
                      overrides: profile.overrides.filter((_, j) => j !== i),
                    })
                  }
                >
                  Remove
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                setProfile({
                  ...profile,
                  overrides: [...profile.overrides, { date: "", windows: [] }],
                })
              }
            >
              Add day off
            </Button>
          </fieldset>
          <Button disabled={working || !!overlapDays.length}>
            Save availability
          </Button>
        </form>
      )}
      {loaded && tab === "meeting types" && (
        <div className="grid lg:grid-cols-2 gap-5">
          <section className={panelClass}>
            <h2 className="text-xl font-semibold">Meeting types</h2>
            <p className="text-sm text-text-secondary">
              Select a meeting type to edit it.
            </p>
            {profile.event_types.map((t) => (
              <button
                key={t.id}
                type="button"
                aria-current={t.id === editing.id}
                onClick={() => {
                  setEditing(t);
                  setTypeSaved("");
                }}
                className={
                  "block w-full text-left border-t border-white/10 py-3 px-2 rounded-lg " +
                  (t.id === editing.id ? "bg-emerald-500/10" : "")
                }
              >
                <span className="font-semibold">{t.title}</span>
                <p className="text-text-secondary text-sm">
                  {t.duration} minutes ·{" "}
                  {t.providers.map(providerName).join(", ")}
                </p>
                {hiddenReason(t) ? (
                  <p className="text-amber-300 text-sm">{hiddenReason(t)}</p>
                ) : (
                  <p className="text-emerald-400 text-sm">Visible to guests</p>
                )}
              </button>
            ))}
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setEditing(newType(ready));
                setTypeSaved("");
              }}
            >
              New meeting type
            </Button>
          </section>
          <form
            className={panelClass}
            onSubmit={(e) => {
              e.preventDefault();
              void run(async () => {
                const { id, ...value } = editing;
                const result = await api.put<{ id: string }>(
                  `/bookings/types/${id}`,
                  value,
                );
                // Leave "new" at once, so a failed reload cannot lead to a duplicate on the next save.
                setEditing((e) => ({ ...e, id: result.id }));
                const fresh = await reload();
                // Keep the saved type open so the form shows what was stored.
                setEditing(
                  fresh.profile?.event_types.find((t) => t.id === result.id) ?? {
                    ...editing,
                    id: result.id,
                  },
                );
                setTypeSaved(`Saved “${value.title}”.`);
              });
            }}
          >
            <h2 className="text-xl font-semibold">
              {editing.id === "new"
                ? "New meeting type"
                : `Edit “${profile.event_types.find((t) => t.id === editing.id)?.title ?? editing.title}”`}
            </h2>
            {!saved && (
              <p>Save your booking profile under Availability first.</p>
            )}
            <fieldset disabled={!saved || working} className="space-y-3">
              <label className="block">
                Title
                <input
                  required
                  maxLength={150}
                  className={fieldClass}
                  value={editing.title}
                  onChange={(e) =>
                    setEditing({ ...editing, title: e.target.value })
                  }
                />
              </label>
              <div className="grid sm:grid-cols-2 gap-3">
                {(
                  [
                    {
                      key: "duration",
                      label: "Duration (minutes)",
                      min: 15,
                      max: 180,
                      step: 15,
                    },
                    {
                      key: "buffer_before",
                      label: "Buffer before (minutes)",
                      min: 0,
                      max: 120,
                      step: 1,
                    },
                    {
                      key: "buffer_after",
                      label: "Buffer after (minutes)",
                      min: 0,
                      max: 120,
                      step: 1,
                    },
                    {
                      key: "notice_hours",
                      label: "Minimum notice (hours)",
                      min: 1,
                      max: 720,
                      step: 1,
                    },
                    {
                      key: "horizon_days",
                      label: "Book ahead (days)",
                      min: 1,
                      max: 90,
                      step: 1,
                    },
                    {
                      key: "daily_limit",
                      label: "Maximum bookings per day",
                      min: 1,
                      max: 40,
                      step: 1,
                    },
                  ] as const
                ).map((f) => (
                  <label key={f.key}>
                    {f.label}
                    <input
                      required
                      type="number"
                      min={f.min}
                      max={f.max}
                      step={f.step}
                      className={fieldClass}
                      value={editing[f.key]}
                      onChange={(e) =>
                        setEditing({
                          ...editing,
                          [f.key]: Number(e.target.value),
                        })
                      }
                    />
                  </label>
                ))}
              </div>
              <p className="text-sm text-text-secondary">
                Meeting platform. Guests only see platforms that are connected
                under Calendars.
              </p>
              {allProviders.map((p) => {
                const isReady = ready.includes(p);
                const checked = editing.providers.includes(p);
                const c = connections.find((v) => v.provider === p);
                const why = isReady
                  ? ""
                  : p === "titan"
                    ? !c?.connected
                      ? "connect your Titan calendar under Calendars"
                      : !c.settings?.meetingSupported
                        ? "add your meeting link under Calendars"
                        : "booking email is not set up — ask an Admin"
                    : c?.configured
                      ? "connect it under Calendars"
                      : "not available yet";
                return (
                  <label
                    key={p}
                    className={"flex gap-2" + (isReady ? "" : " opacity-60")}
                  >
                    <input
                      type="checkbox"
                      // Unready platforms can still be unticked, never newly ticked.
                      disabled={!isReady && !checked}
                      checked={checked}
                      onChange={(e) =>
                        setEditing({
                          ...editing,
                          providers: e.target.checked
                            ? [...editing.providers, p]
                            : editing.providers.filter((v) => v !== p),
                        })
                      }
                    />
                    <span>
                      {providerName(p)}
                      {why && (
                        <span className="text-text-secondary"> — {why}</span>
                      )}
                    </span>
                  </label>
                );
              })}
              {editing.providers.length > 0 &&
                !editing.providers.some((p) => ready.includes(p)) && (
                  <p role="alert" className="text-sm text-amber-300">
                    Guests will not see this meeting type until one ticked
                    platform is connected.
                  </p>
                )}
              <label className="flex gap-2">
                <input
                  type="checkbox"
                  checked={editing.enabled}
                  onChange={(e) =>
                    setEditing({ ...editing, enabled: e.target.checked })
                  }
                />
                Enabled
              </label>
            </fieldset>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                disabled={!saved || working || !editing.providers.length}
              >
                Save meeting type
              </Button>
              {editing.id !== "new" && (
                <Button
                  type="button"
                  variant="ghost"
                  disabled={working}
                  onClick={() => {
                    if (!window.confirm(`Delete “${storedType?.title}”?`)) return;
                    void run(async () => {
                      await api.delete(`/bookings/types/${editing.id}`);
                      await reload();
                      setEditing(newType(ready));
                      setTypeSaved("");
                      setNotice("Meeting type deleted.");
                    });
                  }}
                >
                  Delete
                </Button>
              )}
              <span role="status" className="text-sm">
                {typeSaved && !typeDirty ? (
                  <span className="text-emerald-400">{typeSaved}</span>
                ) : editing.id !== "new" && typeDirty ? (
                  <span className="text-amber-300">Unsaved changes</span>
                ) : null}
              </span>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
